"use client";

import { useState, useEffect } from "react";
import { Cpu, Radio, Settings2, Wrench, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const servicesEn = [{
  sn: "SVC/01",
  icon: Wrench,
  title: "Research & Development",
  description: "End-to-end product engineering, from initial concept sketches to functional prototypes and final design optimization."
}, {
  sn: "SVC/02",
  icon: Settings2,
  title: "Factory Production",
  description: "Full-scale manufacturing across our specialized plastic and plush facilities with rigorous quality control standards."
}, {
  sn: "SVC/03",
  icon: Radio,
  title: "Compliance & Testing",
  description: "Comprehensive safety and quality assurance testing through global partners including BV, ITS, SGS, and TUV-R."
}, {
  sn: "SVC/04",
  icon: Cpu,
  title: "Market Certifications",
  description: "Expert navigation of international regulatory requirements, including CCC for China and G-Mark for Gulf markets."
}];
const servicesZh = [{
  sn: "SVC/01",
  icon: Wrench,
  title: "研發與工程設計",
  description: "端到端產品工程，從初步概念草圖到功能原型製作及最終設計優化。"
}, {
  sn: "SVC/02",
  icon: Settings2,
  title: "工廠生產製造",
  description: "於我們專業的塑膠及毛絨製造基地進行規模化生產製造，恪守嚴格的品質控制標準。"
}, {
  sn: "SVC/03",
  icon: Radio,
  title: "合規與檢測認證",
  description: "透過 BV、ITS、SGS 及 TUV-R 等全球權威合作夥伴，提供全面的安全與品質檢測。"
}, {
  sn: "SVC/04",
  icon: Cpu,
  title: "市場准入認證",
  description: "專業應對國際法規標準，包括中國 CCC 認證及海灣國家 G-Mark 認證。"
}];
export default function Solutions() {
  const [isZh, setIsZh] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsZh(localStorage.getItem("elite-language") === "zh-Hant");
    }
  }, []);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const services = isZh ? servicesZh : servicesEn;
  return <section data-section-id="4998" id="concept-to-shelf-solutions" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-[140px]">
      <div className="pointer-events-none absolute top-8 left-8 hidden md:block">
        <div className="relative h-4 w-4">
          <div className="bg-border absolute top-1/2 left-0 h-px w-4 -translate-y-1/2" />
          <div className="bg-border absolute top-0 left-1/2 h-4 w-px -translate-x-1/2" />
        </div>
      </div>
      <div className="pointer-events-none absolute top-8 right-8 hidden md:block">
        <div className="relative h-4 w-4">
          <div className="bg-border absolute top-1/2 left-0 h-px w-4 -translate-y-1/2" />
          <div className="bg-border absolute top-0 left-1/2 h-4 w-px -translate-x-1/2" />
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div ref={headerRef} className={`border-border mb-16 flex items-center justify-between border-b pb-6 transition-all duration-700 md:mb-24 ${headerVisible ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0"}`}>
          <span className="font-mono text-muted-foreground text-xs tracking-widest uppercase md:text-sm">
            {isZh ? "§ 03 / 核心能力" : "§ 03 / CAPABILITIES"}
          </span>
          <Link to="#" className="text-foreground hover:text-primary group font-mono text-xs tracking-widest uppercase transition-colors md:text-sm">
            {isZh ? "生產製造" : "Manufacturing"}
          </Link>
        </div>

        <div ref={headlineRef} className={`mb-20 transition-all duration-1000 md:mb-28 ${headlineVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <h2 className="font-default flex flex-col text-[18vw] leading-[0.9] font-bold tracking-tighter md:text-[14vw] lg:text-[120px]">
            <span className="block !text-6xl">
              {isZh ? "從概念 /" : "CONCEPT /"}
            </span>
            <span className="block translate-x-[4vw] !text-6xl md:translate-x-12 lg:translate-x-16">
              {isZh ? "至上架 /" : "TO SHELF /"}
            </span>
            <span className="block translate-x-[8vw] !text-6xl md:translate-x-24 lg:translate-x-32">
              {isZh ? "生產製造方案。" : "SOLUTIONS."}
            </span>
          </h2>
          <div className="mt-8 flex items-center gap-4 md:mt-12">
            <div className="bg-primary h-px w-12" />
            <p className="text-muted-foreground font-mono text-xs tracking-wider uppercase md:text-sm">
              {isZh ? "為全球消費品牌提供精密生產製造" : "Precision manufacturing for global consumer brands"}
            </p>
          </div>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((service, i) => {
          const Icon = service.icon;
          const isOffset = i % 2 === 1;
          return <div key={service.sn} data-index={i} className={`group border-border bg-card hover:border-primary relative border p-8 transition-all duration-500 md:p-10 lg:p-10 ${isOffset ? "lg:translate-y-6" : ""} ${gridVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`} style={{
            transitionDelay: gridVisible ? `${i * 120}ms` : "0ms"
          }}>
                <span className="bg-border group-hover:bg-primary absolute top-0 left-0 h-2 w-px transition-colors" />
                <span className="bg-border group-hover:bg-primary absolute top-0 left-0 h-px w-2 transition-colors" />
                <span className="bg-border group-hover:bg-primary absolute right-0 bottom-0 h-2 w-px transition-colors" />
                <span className="bg-border group-hover:bg-primary absolute right-0 bottom-0 h-px w-2 transition-colors" />

                <div className="text-muted-foreground group-hover:text-primary mb-8 font-mono text-xs tracking-widest transition-colors">
                  {service.sn}
                </div>

                <div className="mb-8">
                  <div className="relative inline-flex">
                    <Icon strokeWidth={1} className="text-primary size-10 transition-transform duration-500 group-hover:rotate-90" />
                    <span className="bg-primary/0 group-hover:bg-primary/10 absolute inset-0 -m-3 blur-xl transition-all duration-500" />
                  </div>
                </div>

                <h3 className="font-default text-foreground mb-4 text-2xl leading-tight font-semibold">
                  {service.title}
                </h3>

                <p className="text-muted-foreground mb-10 text-sm leading-relaxed">
                  {service.description}
                </p>

                <Button asChild variant="link" className="text-foreground group-hover:text-primary h-auto p-0 font-mono text-xs tracking-widest uppercase no-underline hover:no-underline">
                  <Link to="#" className="inline-flex items-center gap-1">
                    {isZh ? "了解更多" : "Explore"}
                    <ArrowRight className="size-3 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
              </div>;
        })}
        </div>
      </div>
    </section>;
}